import {
  SelectedItemsListRender,
  RenderMoreOptionsFilter,
  ClearAll,
} from "../sets-filter/index.client";
import { FilterSidebar } from "@components/organisms/filter-sidebar";
import { useNavigate, useUrl, Link } from "@shopify/hydrogen";
import { useFilter } from "@models/interactions/use-filter";
import { useScroll } from "@models/interactions/use-scroll";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { translate } from "@locales";
import {
  CATEGORY_FILTER,
  PIECES_FILTER,
  MARKER_FILTER,
  COLOR_FILTER,
  PRICE_FILTER,
} from "@models/api/filter-options";

function useSidebar() {
  const categoryFilter = useFilter({
    name: "category",
    filter: CATEGORY_FILTER,
  });
  const dependent = useMemo(
    () => ({ filter: categoryFilter, values: ["sets", "markers"] }),
    [categoryFilter]
  );
  const piecesFilter = useFilter({
    name: "pieces",
    filter: PIECES_FILTER,
    multiple: true,
    dependent,
  });
  const colorFilter = useFilter({
    name: "color",
    filter: COLOR_FILTER,
    multiple: true,
    dependent,
  });
  const markerFilter = useFilter({
    name: "marker",
    filter: MARKER_FILTER,
    dependent,
  });
  const priceFilter = useFilter({ name: "price", filter: PRICE_FILTER });
  const navigate = useNavigate();
  const { pathname, searchParams } = useUrl();
  const scroll = useScroll();

  const rendering = useRef(0);
  useEffect(() => {
    if (!rendering.current) {
      rendering.current += 1;
      return;
    }
    scroll.operations.keepScrollTopPosition();
  }, [searchParams]);

  const areDependentValuesSelected = useMemo(
    () =>
      dependent.filter.models.selectedItems.some((item) =>
        dependent.values.some((value) => value === item.value)
      ),
    [dependent.filter.models.selectedItems]
  );

  useEffect(() => {
    if (areDependentValuesSelected) return;
    ["pieces", "color", "marker"].forEach((value) =>
      searchParams.delete(value)
    );
    navigate(`${pathname}?${searchParams.toString()}`);
  }, [areDependentValuesSelected]);

  const clearSelection = useCallback(() => {
    navigate(pathname);
  }, [pathname]);

  const isClearList = useMemo(
    () => [categoryFilter, priceFilter].map((el) => el.models.isClear),
    [categoryFilter, priceFilter]
  );

  const showClearAllButton = useMemo(
    () => isClearList.some((v) => !v),
    isClearList
  );

  return {
    models: {
      showClearAllButton,
      categoryFilter,
      piecesFilter,
      markerFilter,
      priceFilter,
      colorFilter,
    },
    operations: {
      clearSelection,
    },
  };
}

export function Sidebar() {
  const {
    models: { categoryFilter, showClearAllButton, ...models },
    operations,
  } = useSidebar();
  return (
    <>
      <div className="hidden lg:grid">
        <Link to="/" className="text-[12px]">
          {translate("base.home")}/
        </Link>
        <Link to="/shop" className="text-[18px]">
          {translate("shop.filter.our-shop")}
        </Link>
      </div>
      <div className="flex items-center justify-between mt-[30px]">
        <span className="text-[#CDB802] italic invisible lg:visible">
          {translate("shop.filter.filter-by")}:
        </span>
        <ClearAll
          show={showClearAllButton}
          onClick={operations.clearSelection}
        />
      </div>
      <div className="flex gap-1 flex-wrap mt-4 lg:mt-0 max-sm:pb-5">
        <SelectedItemsListRender {...{ ...models, categoryFilter }} />
      </div>
      <hr className="hidden lg:inline-block" />
      <FilterSidebar
        title={translate("shop.filter.product-categories.title")}
        items={categoryFilter.models.filter}
        selectItem={categoryFilter.operations.selectItemByIndex}
        multiple={categoryFilter.models.multiple}
        renderInputRadio
      />
      <RenderMoreOptionsFilter {...models} />
      <br className="inline-block lg:hidden" />
      <span className="text-dark-gray mt-9 text-sm hidden">
        {translate("catalog.filter.hint")}
      </span>
    </>
  );
}
